import productService from '../../../../services/products';
import tacHelpers from '../../tacHelpers';
import getItemConfig from '../../../../scene/util/getItemConfig';
import { getDependencyDiff, getDependentItems } from './dependentItems';
import geometry from '../../../../scene/util/geometry';
import constants from '../../../../settings/constants';
import { unique } from '../../../../util/array';
import { ITEMS } from '../../../../constants';

/**
 * Function called automatically to filter the slots available for sections in aurdal
 *
 * @param {Array} slots list of slots available to drop per section
 * @return {Boolean} Weither if thats a droppable or not it will return true or false
 */
function filterSlots(slots) {
  const NUMBER_OF_SLOTS = 24;
  const UNAVAILABLE_SLOTS = 2;
  const NUMBER_OF_FREE_SLOTS = (NUMBER_OF_SLOTS - UNAVAILABLE_SLOTS) / 2;

  return slots.filter(slot => {
    if (productService.isType(slot, ITEMS.DRAWER)) {
      return +slot.y < +slot.height * NUMBER_OF_FREE_SLOTS;
    }

    return true;
  });
}

function getAllowedOverlap(tac, item, relative) {
  const sectionRelation =
    productService.isType(item, ITEMS.SECTION) &&
    productService.isType(relative, ITEMS.SECTION);

  if (sectionRelation) {
    const tacItem = tacHelpers.getItem(tac, item.itemid);
    const sidePanelWidth = 20;

    if (
      tacItem &&
      tacHelpers.hasExtClothesRail(tacItem) &&
      tacHelpers.isClothesRailConnected({ items: [tacItem] }, relative)
    ) {
      // the dragged item has an ext cr connected to the current relative
      return {
        x: 0,
        y: 0,
        z: 0,
        width: -sidePanelWidth,
        height: 0,
        depth: 0,
      };
    } else if (
      tacItem &&
      tacHelpers.hasExtClothesRail(relative) &&
      tacHelpers.isClothesRailConnected({ items: [relative] }, tacItem)
    ) {
      // the current relative has an ext cr connected to the dragged item
      return { x: sidePanelWidth, y: 0, z: 0, width: 0, height: 0, depth: 0 };
    }

    // two sections with no interfering clothes rails
    return {
      x: sidePanelWidth,
      y: 0,
      z: 0,
      width: -sidePanelWidth * 2,
      height: 0,
      depth: 0,
    };
  }

  return { x: 0, y: 0, z: 0, width: 0, height: 0, depth: 0 };
}

function getNeighbouringSections(tac, section, direction) {
  return tacHelpers
    .getSections(tac)
    .filter(
      cand =>
        cand.itemid !== section.itemid &&
        geometry.collides(cand, section) &&
        (!direction ||
          (direction === 'right' && cand.x > section.x) ||
          (direction === 'left' && cand.x < section.x))
    );
}

function getSlotSources(item, tac) {
  //Not implemented for aurdal
  return [];
}

function getSnappingPosition(item, rects) {
  if (!productService.isSection(item)) {
    return;
  }

  const postWidth = productService.getPostWidth();
  const sectionRects = rects.filter(productService.isSection);
  const closeRects = geometry.getCollidingRects(item, sectionRects, {
    right: constants.SECTION_SNAPPING_DISTANCE + postWidth - 1,
    left: constants.SECTION_SNAPPING_DISTANCE + postWidth - 1,
  });

  const partner = geometry.closestCollidingRect(item, closeRects);

  if (!partner) {
    return;
  }

  if (partner.x < item.x) {
    return { x: partner.x + partner.width };
  } else if (partner.x > item.x) {
    return { x: partner.x - item.width };
  }
}

function getPartnerConfig(partner) {
  return getItemConfig(partner);
}

function isIncompleteSection(item) {
  return (
    productService.isSection(item) &&
    item.items.filter(item => productService.isType(item, 'side-panel'))
      .length < 2
  );
}

function isMultiColouredSection(section, tac) {
  if (!isIncompleteSection(section)) {
    return false;
  }

  const sidePanels = section.items.filter(item =>
    productService.isType(item, 'side-panel')
  );

  let neighboursOfInterest;
  if (!sidePanels.length) {
    neighboursOfInterest = getNeighbouringSections(tac, section);
  } else if (!sidePanels.find(side => side.x === 0)) {
    neighboursOfInterest = getNeighbouringSections(tac, section, 'left');
  } else {
    neighboursOfInterest = getNeighbouringSections(tac, section, 'right');
  }

  return neighboursOfInterest.some(
    neighbour => neighbour.filter.color !== section.filter.color
  );
}

function partnerSlots(slots, otherSlots, tac) {
  const config = getItemConfig(slots[0]);
  // add complement slot for other half of extendable clothes rod
  const insideSlots = slots.filter(slot => slot.id === config.innerId);
  if (insideSlots.length) {
    const outsideSlots = otherSlots;
    insideSlots.forEach(left => {
      const fits = outsideSlots.filter(
        right =>
          right.x > left.x &&
          right.y === left.y &&
          right.x + right.width <= left.x + config.maxWidth
      );
      if (fits.length === 1) {
        left.partnerSlot = fits[0];
      }
      if (fits.length > 1) {
        throw new Error('found more than one fit for outside clothes rail');
      }
    });
  }
  return slots.filter(slot => slot.id !== config.innerId || slot.partnerSlot);
}

function moveWithCr(item) {
  return productService.isSection(item) || tacHelpers.hasExtClothesRail(item);
}

function findSuperSection(tac, member, skiplist) {
  // pad items just a little so they overlap a bit with stuff they touch
  const padding = {
    right: 1,
    left: 1,
  };

  const superSection = [member];

  const candidates = tac.items.filter(
    item =>
      item.itemid !== member.itemid && skiplist.indexOf(item.itemid) === -1
  );

  const fullSizeMember = tacHelpers.getFullSize(member);

  if (productService.isType(member, [ITEMS.SECTION, 'mounting-rail'])) {
    // set padding to 0 so we only collide with merged sections, if no cr
    padding.left = tacHelpers.isClothesRailConnected(tac, member) ? 1 : 0;
    padding.right = tacHelpers.hasExtClothesRail(member) ? 1 : 0;
  }

  const collisions = candidates.filter(candidate =>
    geometry.collides(
      fullSizeMember,
      tacHelpers.getFullSize(candidate),
      padding
    )
  );
  const skip = skiplist
    .concat(collisions.map(item => item.itemid))
    .filter(unique);

  const rest = collisions.map(collision =>
    findSuperSection(tac, collision, skip)
  );

  return Array.prototype.concat.apply(superSection, rest).filter(unique);
}

function getWallResizingLimits(tac) {
  const limits = tacHelpers.getLimits(tac);

  const allSections = tacHelpers.getSections(tac).sort((a, b) => b.x - a.x);
  const rightmostSection = allSections[0];

  if (!rightmostSection) {
    //no sections in tac
    return limits;
  }

  const superSection = findSuperSection(tac, rightmostSection, []);

  if (
    superSection.filter(
      member =>
        productService.isType(member, 'sidewall') &&
        (tacHelpers.hasExtClothesRail(member) ||
          tacHelpers.isClothesRailConnected(tac, member))
    ).length > 1
  ) {
    limits.blockedX = true;
  }

  const outsidersLimits = tacHelpers.getLimits({
    items: tac.items.filter(
      item => !superSection.some(member => member.itemid === item.itemid)
    ),
  });

  const condensed = tacHelpers.getCondensedTacWidth({ items: superSection });
  if (outsidersLimits.max.x > 0) {
    limits.max.x = outsidersLimits.max.x + condensed;
  } else {
    limits.min.x = 0;
    limits.max.x = condensed;
  }
  return limits;
}

const api = {
  filterSlots,
  findSuperSection,
  getAllowedOverlap,
  getDependencyDiff,
  getDependentItems,
  getNeighbouringSections,
  getPartnerConfig,
  getSlotSources,
  getSnappingPosition,
  isIncompleteSection,
  isMultiColouredSection,
  getWallResizingLimits,
  moveWithCr,
  partnerSlots,
};

export default api;
